import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TimelineColumn from './components/TimelineColumn';
import ActionFormModal from './components/ActionFormModal';
import ImplementationMetrics from './components/ImplementationMetrics';
import RiskIndicators from './components/RiskIndicators';
import ExportPanel from './components/ExportPanel';

const ActionPlanDashboard = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [defaultPeriod, setDefaultPeriod] = useState(30);

  const [actions, setActions] = useState([
    {
      id: 1,
      title: "Implementasi Sistem Monitoring Pasokan Real-time",
      description: "Mengembangkan dan mengimplementasikan dashboard monitoring pasokan susu dari farmer dengan update real-time untuk deteksi dini fluktuasi pasokan dan kualitas.",
      category: "supply",
      priority: "critical",
      responsible: "Kepala Operasional",
      period: 30,
      budget: 75000000,
      dependencies: ["Infrastruktur IT", "Training Tim"],
      kpis: [
        "100% farmer terintegrasi dalam sistem",
        "Response time < 2 jam untuk anomali pasokan",
        "Akurasi prediksi pasokan > 90%"
      ],
      progress: 35
    },
    {
      id: 2,
      title: "Diversifikasi Base Supplier Susu",
      description: "Menambah 15 farmer baru dari wilayah berbeda untuk mengurangi risiko konsentrasi pasokan dan meningkatkan resiliensi supply chain.",
      category: "supply",
      priority: "high",
      responsible: "Manajer Procurement",
      period: 30,
      budget: 50000000,
      dependencies: ["Survey Lokasi", "Kontrak Kerjasama"],
      kpis: [
        "15 farmer baru terverifikasi",
        "Distribusi geografis > 3 wilayah",
        "Kapasitas pasokan meningkat 25%"
      ],
      progress: 20
    },
    {
      id: 3,
      title: "Upgrade Sistem Pendingin Utama",
      description: "Mengganti chiller utama dengan kapasitas 2x lipat untuk mendukung scale-up produksi dan mengurangi risiko kegagalan pendinginan.",
      category: "equipment",
      priority: "critical",
      responsible: "Kepala Teknik",
      period: 60,
      budget: 250000000,
      dependencies: ["Vendor Selection", "Shutdown Planning"],
      kpis: [
        "Kapasitas pendinginan 5000L/hari",
        "Downtime < 24 jam",
        "Efisiensi energi meningkat 30%"
      ],
      progress: 15
    },
    {
      id: 4,
      title: "Optimasi Cashflow Management System",
      description: "Implementasi sistem manajemen cashflow terintegrasi untuk monitoring real-time arus kas, prediksi kebutuhan modal kerja, dan optimasi payment terms dengan supplier.",
      category: "financial",
      priority: "high",
      responsible: "CFO",
      period: 60,
      budget: 45000000,
      dependencies: ["Software Selection", "Data Migration"],
      kpis: [
        "Visibilitas cashflow real-time",
        "Akurasi forecast > 85%",
        "Working capital cycle -15 hari"
      ],
      progress: 40
    },
    {
      id: 5,
      title: "Ekspansi Kanal Distribusi Modern Trade",
      description: "Membuka kerjasama dengan 5 retail chain besar untuk mengurangi dependency pada institutional buyer dan meningkatkan margin.",
      category: "market",
      priority: "medium",
      responsible: "Direktur Sales",
      period: 90,
      budget: 120000000,
      dependencies: ["Product Certification", "Marketing Campaign"],
      kpis: [
        "5 retail partner baru",
        "Kontribusi revenue modern trade 30%",
        "Margin improvement 8%"
      ],
      progress: 10
    },
    {
      id: 6,
      title: "Pembentukan Quality Control Team Dedicated",
      description: "Merekrut dan melatih tim QC khusus dengan 5 personel untuk meningkatkan standar kualitas dan mengurangi rejection rate.",
      category: "governance",
      priority: "high",
      responsible: "Head of Quality",
      period: 60,
      budget: 85000000,
      dependencies: ["Recruitment", "Lab Setup"],
      kpis: [
        "5 QC staff terlatih",
        "Rejection rate < 3%",
        "Compliance score > 95%"
      ],
      progress: 25
    },
    {
      id: 7,
      title: "Implementasi Preventive Maintenance Program",
      description: "Mengembangkan dan menjalankan program maintenance preventif untuk semua equipment kritis guna mengurangi downtime dan memperpanjang umur aset.",
      category: "operational",
      priority: "medium",
      responsible: "Maintenance Manager",
      period: 90,
      budget: 35000000,
      dependencies: ["Equipment Audit", "Spare Parts Inventory"],
      kpis: [
        "Downtime reduction 40%",
        "MTBF meningkat 50%",
        "Maintenance cost -20%"
      ],
      progress: 30
    },
    {
      id: 8,
      title: "Pengadaan Backup Generator 150 KVA",
      description: "Instalasi generator backup untuk memastikan kontinuitas operasi saat gangguan listrik dan melindungi produk dari kerusakan.",
      category: "equipment",
      priority: "critical",
      responsible: "Kepala Teknik",
      period: 30,
      budget: 180000000,
      dependencies: ["Site Preparation", "Electrical Upgrade"],
      kpis: [
        "Uptime 99.9%",
        "Switchover time < 10 detik",
        "Zero product loss dari power failure"
      ],
      progress: 50
    }
  ]);

  const handleAddAction = (period) => {
    setDefaultPeriod(period);
    setEditingAction(null);
    setIsModalOpen(true);
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setIsModalOpen(true);
  };

  const handleDeleteAction = (actionId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus aksi ini?')) {
      setActions(prev => prev?.filter(a => a?.id !== actionId));
    }
  };

  const handleSaveAction = (actionData) => {
    if (editingAction) {
      setActions(prev => prev?.map(a => a?.id === actionData?.id ? actionData : a));
    } else {
      setActions(prev => [...prev, actionData]);
    }
  };

  const handleUpdateProgress = (actionId, newProgress) => {
    setActions(prev => prev?.map(a => 
      a?.id === actionId ? { ...a, progress: newProgress } : a
    ));
  };

  const handleExport = (exportData) => {
    console.log('Exporting action plan:', exportData);
    alert(`Rencana aksi berhasil diekspor dalam format ${exportData?.format?.toUpperCase()}\n\nTotal aksi: ${exportData?.actions?.length}`);
  };

  const getActionsByPeriod = (period) => {
    return actions?.filter(a => a?.period === period);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20">
        <div className="container-executive py-8 md:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                Dashboard Rencana Aksi 30-60-90 Hari
              </h1>
              <p className="text-muted-foreground">
                Roadmap implementasi komprehensif dengan tracking KPI dan mitigasi risiko
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/executive-report')}
              >
                Kembali ke Laporan
              </Button>
              <Button
                variant="default"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/decision-matrix')}
              >
                Matriks Keputusan
              </Button>
            </div>
          </div>

          <ImplementationMetrics actions={actions} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Calendar" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Timeline Implementasi</h2>
                      <p className="text-sm text-muted-foreground">Rencana aksi berdasarkan periode target</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <TimelineColumn
                    period={30}
                    actions={getActionsByPeriod(30)}
                    onAddAction={handleAddAction}
                    onEditAction={handleEditAction}
                    onDeleteAction={handleDeleteAction}
                    onUpdateProgress={handleUpdateProgress}
                  />
                  <TimelineColumn
                    period={60}
                    actions={getActionsByPeriod(60)}
                    onAddAction={handleAddAction}
                    onEditAction={handleEditAction}
                    onDeleteAction={handleDeleteAction}
                    onUpdateProgress={handleUpdateProgress}
                  />
                  <TimelineColumn
                    period={90}
                    actions={getActionsByPeriod(90)}
                    onAddAction={handleAddAction}
                    onEditAction={handleEditAction}
                    onDeleteAction={handleDeleteAction}
                    onUpdateProgress={handleUpdateProgress}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <RiskIndicators actions={actions} />
              <ExportPanel actions={actions} onExport={handleExport} />
            </div>
          </div>

          <div className="mt-8 bg-accent/10 rounded-xl border border-accent/20 p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Icon name="Lightbulb" size={24} className="text-accent-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">Panduan Implementasi</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Prioritaskan aksi kritis dalam 30 hari pertama untuk quick wins dan momentum</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Monitor progress mingguan dan adjust timeline berdasarkan realisasi lapangan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Alokasikan resource memadai untuk aksi dengan dependensi kompleks</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="CheckCircle2" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>Dokumentasikan lessons learned untuk continuous improvement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <ActionFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAction(null);
        }}
        onSave={handleSaveAction}
        editAction={editingAction}
        defaultPeriod={defaultPeriod}
      />
    </div>
  );
};

export default ActionPlanDashboard;